'use strict';

// external imports
import React, {
    useMemo
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import {keys} from 'ts-transformer-keys';

// prime react imports
import {Toolbar as PrimeToolbar} from 'primereact/toolbar';

// shared imports
import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';

// frontend imports
import {
    getItemTypeDisplayName
} from '~/frontend/src/utilities/renderHelpers';
import {
    getToolbarComponents,
    ToolbarComponents,
    ToolbarLayoutComponents
} from '~/frontend/src/utilities/toolbars';

import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import {overviewUtilities} from '~/frontend/src/components/base/OverviewDesigner/utilities';


export type DefaultProps = {};
export const defaultProps : Required<DefaultProps> = {};

type Props = {
    dataContext : string;

    overviewId : number;
    overviewKey : string;
    overviewName : string;

    style? : CssStyle;
};

export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = DefaultProps & Props;


/**
 * Headerbar
 */
const Headerbar = ( props : AllProps ) => {
    const dispatch : AppDispatch = useDispatch(),
          {
              dataContext,
              overviewId,
              overviewKey,
              overviewName,
              style
          }                      = props,
          user                   = useSelector( ( state : StoreState ) => state.session?.user ),
          language               = user
              ? user.language
              : undefined,
          overview               = global.data[ dataContext ]?.items[ overviewId ],
          query                  = useMemo(
              () => {
                  return overview ? overviewUtilities.getQuery( dataContext, overview.id ) : undefined;
              },
              [
                  dataContext,
                  overview
              ]
          ),
          dataStructure          = useMemo(
              () => {
                  return query?.dataStructure;
              },
              [
                  query
              ]
          ),
          rootDataObjectSchema   = useMemo(
              () => {
                  return ( dataStructure?.item.objectSchema ?? 'DATA' ).toUpperCase() as Schema;
              },
              [
                  dataStructure
              ]
          ),
          rootDataObjectItemType = useMemo(
              () => {
                  return dataStructure?.item.objectType;
              },
              [
                  dataStructure
              ]
          ),
          itemType               = useMemo(
              () => {
                  if ( rootDataObjectSchema && rootDataObjectItemType ) {
                      return metaModel[ rootDataObjectSchema ].getItemType( rootDataObjectItemType );
                  }
                  else {
                      return undefined;
                  }
              },
              [
                  dataStructure
              ]
          );

    return useMemo(
        () => {
            const layoutComponents : ToolbarLayoutComponents = {
                left : [],
                right: []
            };

            // left
            layoutComponents.left.push( {
                type  : 'genericNode',
                params: <label
                    key="title"
                    className="yn-label yn-label-value"
                >
                    {__( 'YN.base.frontend.overviewDesigner.header.title' )}
                </label>
            } );

            if ( layoutComponents.left.length ) {
                layoutComponents.left.push( {
                    type  : 'genericNode',
                    params: <i key={`separator_${layoutComponents.left.length}`} className="p-toolbar-separator ml-4 mr-4"/>
                } );
            }

            layoutComponents.left.push( {
                type  : 'genericNode',
                params: <label
                    key="overviewItemLabel"
                    className="yn-label yn-label-key mr-2"
                >
                    {__( 'YN.base.frontend.overviewDesigner.header.overview.label' )}
                </label>
            } );
            layoutComponents.left.push( {
                type  : 'genericNode',
                params: <label
                    key="overviewItem"
                    className="yn-label yn-label-value"
                >
                    {metaModel[ 'CFG' ].getItemType( 'Overview' )?.displayAttribute === 'key' ? overviewKey : overviewName}
                </label>
            } );

            if ( itemType ) {
                if ( layoutComponents.left.length ) {
                    layoutComponents.left.push( {
                        type  : 'genericNode',
                        params: <i key={`separator_${layoutComponents.left.length}`} className="p-toolbar-separator ml-4 mr-4"/>
                    } );
                }

                layoutComponents.left.push( {
                    type  : 'genericNode',
                    params: <label
                        key="itemType"
                        className="yn-label yn-label-key mr-2"
                    >
                        {__( 'YN.base.frontend.overviewDesigner.header.itemType.label' )}
                    </label>
                } );
                layoutComponents.left.push( {
                    type  : 'genericNode',
                    params: <label
                        key="rootDataObjectItemType"
                        className="yn-label yn-label-value"
                    >
                        {getItemTypeDisplayName( itemType.id, rootDataObjectSchema )}
                    </label>
                } );
            }
            else if ( query?.type ) {
                if ( layoutComponents.left.length ) {
                    layoutComponents.left.push( {
                        type  : 'genericNode',
                        params: <i key={`separator_${layoutComponents.left.length}`} className="p-toolbar-separator ml-4 mr-4"/>
                    } );
                }

                layoutComponents.left.push( {
                    type  : 'genericNode',
                    params: <label
                        key="query"
                        className="yn-label yn-label-key mr-2"
                    >
                        {__( 'YN.base.frontend.overviewDesigner.header.query.label' )}
                    </label>
                } );
                layoutComponents.left.push( {
                    type  : 'genericNode',
                    params: <label
                        key="queryType"
                        className="yn-label yn-label-value"
                    >
                        {__( `YN.base.frontend.overviewDesigner.query.${query.type.toLowerCase()}.label` )}
                    </label>
                } );
            }

            const toolbarComponents : ToolbarComponents = getToolbarComponents( layoutComponents );

            return <PrimeToolbar
                start={toolbarComponents.left}
                end={toolbarComponents.right}
                className="yn-panel-headerbar yn-toolbar-horizontal-separator-bottom"
                style={{
                    zIndex: 6,
                    ...style
                }}
            />;
        },
        [
            itemType,
            language,
            overviewKey,
            overviewName,
            query,
            rootDataObjectSchema
        ]
    );
};

Headerbar.defaultProps = defaultProps;
Headerbar.displayName = 'OverviewDesigner-Headerbar';

export default Headerbar;
